import { ProviderAccount } from '@prisma/client'
import { message } from 'antd'
import { ComponentProps, useState } from 'react'
import { Api } from '~/core/trpc'
import { ProviderClient } from '../../providers/abstract/provider.client'
import { ModalAuthentication } from '../components/ModalAuthentication'
import { useStrategyOauth2 } from '../strategy/useStrategyOauth2'

type Props = {
  provider: ProviderClient
  providerAccount: ProviderAccount
  scope: string
  extraAuthParams?: Record<string, any>
  onSubmit?: (providerAccount: ProviderAccount) => void
  onCancel?: () => void
}

type PropsModal = ComponentProps<typeof ModalAuthentication>

export const useAuthorization = ({
  provider,
  providerAccount,
  scope,
  extraAuthParams,
  onSubmit,
  onCancel,
}: Props) => {
  const [isModalOpen, setModalOpen] = useState(false)

  const providerAccountId = providerAccount?.id

  const oauth2 = useStrategyOauth2()

  const claim = Api.dataConnector.claimAccessToken.useMutation()

  const updateAccountMutation = Api.providerAccount.update.useMutation()

  const createAccountScopeMutation =
    Api.providerAccountScope.create.useMutation()
  const updateAccountScopeMutation =
    Api.providerAccountScope.update.useMutation()

  const providerAccountScopeQuery =
    Api.providerAccountScope.findUnique.useQuery(
      {
        where: { scope_providerAccountId: { scope, providerAccountId } },
      },
      { enabled: !!providerAccountId },
    )

  const providerAccountScope = providerAccountScopeQuery?.data

  const updateAccount = async (values: Partial<ProviderAccount>) => {
    const connection: any = values?.connection
    const hasRefreshToken = !!connection?.refresh_token

    await updateOrCreateAccountScope()

    return updateAccountMutation.mutateAsync({
      where: { id: providerAccount?.id },
      data: {
        connection: hasRefreshToken ? values.connection : undefined,
        status: 'ACTIVE',
      },
    })
  }

  const updateOrCreateAccountScope = async () => {
    if (providerAccountScope) {
      return updateAccountScopeMutation.mutateAsync({
        where: { id: providerAccountScope?.id },
        data: {
          status: 'ACTIVE',
        },
      })
    }

    return createAccountScopeMutation.mutateAsync({
      data: {
        providerAccountId,
        scope,
        status: 'ACTIVE',
      },
    })
  }

  const isScopeGranted = values => {
    const scopes: string[] = values?.connection?.scope.split(' ')

    return scopes?.includes(scope)
  }

  const handleOauth2 = async (input?: any) => {
    try {
      const response = await oauth2.start(provider, {
        input,
        scopes: [scope],
        extraParams: extraAuthParams,
      })

      const values = await claim.mutateAsync({
        providerKey: provider.key,
        payload: response,
      })

      if (!isScopeGranted(values)) {
        onCancel?.()
        setModalOpen(false)
        return
      }

      const accountUpdated = await updateAccount(values)

      onSubmit(accountUpdated)
    } catch (error) {
      message.error(`Something went wrong`)
      console.error('Unknown Error', error)
    }
  }

  const start = async () => {
    await handleOauth2()
  }

  const propsModal: PropsModal = {
    isOpen: isModalOpen,
    provider: provider,
    providerAccount: providerAccount,
    onSubmitInput: async values => {
      setModalOpen(false)

      await handleOauth2(values)
    },
    onSubmit: async account => {
      try {
        if (!isScopeGranted(account)) {
          onCancel?.()
          setModalOpen(false)
          return
        }

        const accountUpdated = await updateAccount(account)

        onSubmit?.(accountUpdated)

        setModalOpen(false)
      } catch (error) {
        message.error(`Something went wrong`)
        console.error('Unknown Error', error)
      }
    },
    onCancel: () => {
      onCancel?.()
      setModalOpen(false)
    },
  }

  return {
    start,
    propsModal,
  }
}
